/* [PATCH dobavnica-cmr-fe v1] Dobavnica / CMR v maski dokumenta: prejemnik blaga (naslov dostave) in paketi (packing list).
   API: poslovni_dok.prejemnik {naziv, naslov, posta, kraj, drzava}, paketi [{oznaka, opis, embalaza, kosov, visina_mm, sirina_mm,
   dolzina_mm, prostornina_m3, neto_kg, bruto_kg}] (backend dokumenti._prejemnik_iz_vnosa / _paketi_iz_vnosa; prostornino
   izračuna strežnik, če je ni). Številke v polju z vejico, na API s piko (zaApi). */
import React from 'react';
import { Plus, Trash2, Copy } from 'lucide-react';
import { novKljuc, stevilo, stevSl, zaApi } from '../../lib/izdajaZneski';

export type Prejemnik = { naziv: string; naslov: string; posta: string; kraj: string; drzava: string };
export type Paket = {
  kljuc: string; oznaka: string; opis: string; embalaza: string; kosov: string;
  visina_mm: string; sirina_mm: string; dolzina_mm: string; neto_kg: string; bruto_kg: string;
};

export const PRAZEN_PREJEMNIK: Prejemnik = { naziv: '', naslov: '', posta: '', kraj: '', drzava: '' };

export function prejemnikIzServerja(p: any): Prejemnik {
  const x = p || {};
  return { naziv: x.naziv || '', naslov: x.naslov || '', posta: x.posta || '', kraj: x.kraj || '', drzava: x.drzava || '' };
}

export function prejemnikZaServer(p: Prejemnik): Record<string, string> {
  const out: Record<string, string> = {};
  (Object.keys(PRAZEN_PREJEMNIK) as (keyof Prejemnik)[]).forEach(k => {
    const v = String(p[k] || '').trim();
    if (v) out[k] = k === 'drzava' ? v.toUpperCase().slice(0, 2) : v;
  });
  return out;
}

export function novPaket(prejsnji?: Paket): Paket {
  return { kljuc: novKljuc(), oznaka: prejsnji?.oznaka || '', opis: '', embalaza: prejsnji?.embalaza || '', kosov: '',
    visina_mm: prejsnji?.visina_mm || '', sirina_mm: prejsnji?.sirina_mm || '', dolzina_mm: prejsnji?.dolzina_mm || '', neto_kg: '', bruto_kg: '' };
}

export function paketIzServerja(p: any): Paket {
  return { kljuc: novKljuc(), oznaka: p.oznaka || '', opis: p.opis || '', embalaza: p.embalaza || '', kosov: stevSl(p.kosov),
    visina_mm: stevSl(p.visina_mm), sirina_mm: stevSl(p.sirina_mm), dolzina_mm: stevSl(p.dolzina_mm),
    neto_kg: stevSl(p.neto_kg), bruto_kg: stevSl(p.bruto_kg) };
}

export function paketZaServer(p: Paket) {
  return { oznaka: p.oznaka.trim() || null, opis: p.opis.trim() || null, embalaza: p.embalaza.trim() || null,
    kosov: zaApi(p.kosov), visina_mm: zaApi(p.visina_mm), sirina_mm: zaApi(p.sirina_mm), dolzina_mm: zaApi(p.dolzina_mm),
    neto_kg: zaApi(p.neto_kg), bruto_kg: zaApi(p.bruto_kg) };
}

/** Prostornina paketa v m³ (kosov × v × š × d / 1e9) — samo prikaz; strežnik jo shrani na 4 decimalke. */
export function prostornina(p: Paket): number {
  const k = stevilo(p.kosov), v = stevilo(p.visina_mm), s = stevilo(p.sirina_mm), d = stevilo(p.dolzina_mm);
  return k && v && s && d ? (k * v * s * d) / 1e9 : 0;
}

const inp = 'w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:opacity-70';
const inpR = inp + ' text-right';
const lbl = 'block text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5';
const r3 = (n: number) => Math.round(n * 1000) / 1000;
const st3 = (n: number) => n.toLocaleString('de-DE', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

export function PrejemnikBlaga({ prejemnik, onChange, kupec, readonly }: {
  prejemnik: Prejemnik; onChange: (p: Prejemnik) => void; kupec?: any; readonly?: boolean;
}) {
  const nastavi = (k: keyof Prejemnik, v: string) => onChange({ ...prejemnik, [k]: v });
  const izKupca = () => kupec && onChange({ naziv: kupec.naziv || '', naslov: kupec.naslov || '', posta: kupec.posta || '',
    kraj: kupec.kraj || '', drzava: kupec.drzava || '' });
  const prazen = !Object.values(prejemnik).some(v => String(v || '').trim());
  return (
    <div className="rounded-xl border bg-white dark:bg-gray-900 dark:border-gray-700 p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="text-sm font-semibold">Prejemnik blaga (naslov dostave)</div>
        <span className="text-xs text-gray-500">{prazen ? 'prazno = blago prejme kupec' : ''}</span>
        {!readonly && kupec?.naziv && <button type="button" className="ml-auto inline-flex items-center gap-1 text-xs text-blue-600 hover:underline" onClick={izKupca}><Copy className="w-3.5 h-3.5" /> prevzemi od kupca</button>}
        {!readonly && !prazen && <button type="button" className={(kupec?.naziv ? '' : 'ml-auto ') + 'text-xs text-gray-500 hover:text-rose-600 hover:underline'} onClick={() => onChange({ ...PRAZEN_PREJEMNIK })}>izprazni</button>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_minmax(0,2fr)_6rem_minmax(0,1.2fr)_4.5rem] gap-2">
        <div><span className={lbl}>Naziv</span><input className={inp} value={prejemnik.naziv} disabled={readonly} placeholder={kupec?.naziv || 'npr. podjetje c/o špediter'} onChange={e => nastavi('naziv', e.target.value)} /></div>
        <div><span className={lbl}>Naslov</span><input className={inp} value={prejemnik.naslov} disabled={readonly} onChange={e => nastavi('naslov', e.target.value)} /></div>
        <div><span className={lbl}>Pošta</span><input className={inp} value={prejemnik.posta} disabled={readonly} onChange={e => nastavi('posta', e.target.value)} /></div>
        <div><span className={lbl}>Kraj</span><input className={inp} value={prejemnik.kraj} disabled={readonly} onChange={e => nastavi('kraj', e.target.value)} /></div>
        <div><span className={lbl}>Država</span><input className={inp + ' uppercase'} value={prejemnik.drzava} maxLength={2} placeholder="SI" disabled={readonly} onChange={e => nastavi('drzava', e.target.value.toUpperCase())} /></div>
      </div>
    </div>
  );
}

export function PaketiMreza({ paketi, onChange, readonly }: { paketi: Paket[]; onChange: (p: Paket[]) => void; readonly?: boolean }) {
  const nastavi = (kljuc: string, k: keyof Paket, v: string) => onChange(paketi.map(p => (p.kljuc === kljuc ? { ...p, [k]: v } : p)));
  const dodaj = () => onChange([...paketi, novPaket(paketi[paketi.length - 1])]);
  const odstrani = (kljuc: string) => onChange(paketi.filter(p => p.kljuc !== kljuc));
  const vsota = (f: (p: Paket) => number) => paketi.reduce((s, p) => s + f(p), 0);
  const G = 'grid items-start gap-1.5 grid-cols-[1.5rem_minmax(7rem,1.2fr)_minmax(6rem,1fr)_5rem_4.5rem_4.5rem_4.5rem_4.5rem_5rem_5.5rem_5.5rem_2rem]';
  return (
    <div className="rounded-xl border bg-white dark:bg-gray-900 dark:border-gray-700 p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="text-sm font-semibold">Paketi (specifikacija pakiranja)</div>
        <span className="text-xs text-gray-500">neobvezno · na dobavnici kot dodatna stran, vsote v nogi</span>
      </div>
      {paketi.length > 0 && (
        <div className="overflow-x-auto">
          <div className={G + ' text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-200 dark:border-gray-700 pb-1 min-w-[56rem]'}>
            <div>#</div><div>Oznaka</div><div>Opis</div><div>Embalaža</div><div className="text-right">Kosov</div>
            <div className="text-right">Višina mm</div><div className="text-right">Širina mm</div><div className="text-right">Dolžina mm</div>
            <div className="text-right">m³</div><div className="text-right">Neto kg</div><div className="text-right">Bruto kg</div><div></div>
          </div>
          {paketi.map((p, i) => (
            <div key={p.kljuc} className={G + ' py-1 border-b border-gray-100 dark:border-gray-800 min-w-[56rem]'}>
              <div className="pt-1.5 text-sm text-gray-400">{i + 1}</div>
              <input className={inp} value={p.oznaka} disabled={readonly} placeholder="npr. sm/jel" onChange={e => nastavi(p.kljuc, 'oznaka', e.target.value)} />
              <input className={inp} value={p.opis} disabled={readonly} onChange={e => nastavi(p.kljuc, 'opis', e.target.value)} />
              <input className={inp} value={p.embalaza} disabled={readonly} placeholder="PAK, EUR-paleta" onChange={e => nastavi(p.kljuc, 'embalaza', e.target.value)} />
              <input className={inpR} value={p.kosov} disabled={readonly} onChange={e => nastavi(p.kljuc, 'kosov', e.target.value)} />
              <input className={inpR} value={p.visina_mm} disabled={readonly} onChange={e => nastavi(p.kljuc, 'visina_mm', e.target.value)} />
              <input className={inpR} value={p.sirina_mm} disabled={readonly} onChange={e => nastavi(p.kljuc, 'sirina_mm', e.target.value)} />
              <input className={inpR} value={p.dolzina_mm} disabled={readonly} onChange={e => nastavi(p.kljuc, 'dolzina_mm', e.target.value)} />
              <div className="pt-1.5 text-right font-mono tabular-nums text-sm">{prostornina(p) ? st3(prostornina(p)) : ''}</div>
              <input className={inpR} value={p.neto_kg} disabled={readonly} onChange={e => nastavi(p.kljuc, 'neto_kg', e.target.value)} />
              <input className={inpR} value={p.bruto_kg} disabled={readonly} onChange={e => nastavi(p.kljuc, 'bruto_kg', e.target.value)} />
              <div className="pt-0.5 text-center">{!readonly && <button type="button" onClick={() => odstrani(p.kljuc)} className="p-1 text-gray-400 hover:text-rose-600" title="Odstrani paket"><Trash2 className="w-4 h-4" /></button>}</div>
            </div>
          ))}
          <div className={G + ' pt-1.5 text-sm font-semibold min-w-[56rem]'}>
            <div></div><div className="col-span-3">Skupaj ({paketi.length})</div>
            <div className="text-right font-mono">{stevSl(r3(vsota(p => stevilo(p.kosov)))) || ''}</div><div></div><div></div><div></div>
            <div className="text-right font-mono">{st3(vsota(prostornina))}</div>
            <div className="text-right font-mono">{stevSl(r3(vsota(p => stevilo(p.neto_kg)))) || ''}</div>
            <div className="text-right font-mono">{stevSl(r3(vsota(p => stevilo(p.bruto_kg)))) || ''}</div><div></div>
          </div>
        </div>
      )}
      {!readonly && (
        <button type="button" onClick={dodaj} className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">
          <Plus className="w-4 h-4" /> Dodaj paket {paketi.length > 0 ? '(prevzame oznako, embalažo in mere prejšnjega)' : ''}
        </button>
      )}
    </div>
  );
}
